// Define the supported product view aspects
const SUPPORTED_VIEW_ASPECTS = ["S", "T", "W"];

// Define the supported product view rotations
const SUPPORTED_VIEW_ROTATIONS = [0, 90, 180, 270];

// Define the default configured role view settings
const DEFAULT_ROLE_VIEW_SETTINGS = {
    aspects: ["S", "T", "W"],
    rotations: [0, 90, 180, 270],
};



// Get the display priority used by the product preview carousel
const getProductPreviewViewPriority = (view) => {

    // Put the explicit reference view first
    if (String(view?.reference || "").trim().toUpperCase() === "Y") {
        return 0;
    }

    // Put default-style views after the reference view
    if (String(view?.style || "").trim().toLowerCase() === "default") {
        return 1;
    }

    // Put every other compatible view after those
    return 2;

};



const normaliseViewSettings = (viewSettings, fallbackViewSettings) => {

    // Use the fallback settings when the supplied value is not an object
    if (!viewSettings || typeof viewSettings !== "object") {
        return {
            aspects: [...fallbackViewSettings.aspects],
            rotations: [...fallbackViewSettings.rotations],
        };
    }

    // Start with empty aspect and rotation lists
    let aspects = [];
    let rotations = [];

    // Normalise supplied aspects
    if (Array.isArray(viewSettings.aspects)) {

        aspects = viewSettings.aspects
            .map((aspect) => String(aspect || "").trim().toUpperCase())
            .filter((aspect) => SUPPORTED_VIEW_ASPECTS.includes(aspect));

        // Remove duplicate aspects while preserving their order
        aspects = Array.from(new Set(aspects));

    } else {

        // Use the fallback aspects when the field is missing
        aspects = [...fallbackViewSettings.aspects];

    }

    // Normalise supplied rotations
    if (Array.isArray(viewSettings.rotations)) {

        rotations = viewSettings.rotations
            .map((rotation) => Number(rotation))
            .filter((rotation) => SUPPORTED_VIEW_ROTATIONS.includes(rotation));

        // Remove duplicate rotations while preserving their order
        rotations = Array.from(new Set(rotations));

    } else {

        // Use the fallback rotations when the field is missing
        rotations = [...fallbackViewSettings.rotations];

    }

    // Return the normalised settings
    return {
        aspects: aspects,
        rotations: rotations,
    };

};



export const normaliseRoleViewSettings = (viewSettings) => {

    // Configured roles without saved settings support every view by default
    return normaliseViewSettings(viewSettings, DEFAULT_ROLE_VIEW_SETTINGS);

};

// Get the supported design aspects for one real product view
export const normaliseProductViewAspects = (view) => {

    // Start with the current multi-aspect field
    let aspects = [];

    if (Array.isArray(view?.aspects)) {

        aspects = view.aspects
            .map((aspect) => String(aspect || "").trim().toUpperCase())
            .filter((aspect) => SUPPORTED_VIEW_ASPECTS.includes(aspect));

        // Remove duplicate aspects while preserving their order
        aspects = Array.from(new Set(aspects));

    }

    // Return every aspect this product view can display
    return aspects;

};

// Get the first live product view allowed by one configured product role
export const getFirstConfiguredProductPreviewView = (canonicalProduct, configuredRole, roleCode) => {

    // A configured product role is required because it owns the available view settings
    if (!configuredRole) { return null; }

    // Accept either a canonical POD product or an already-expanded custom product
    const customProduct = canonicalProduct?.custom_product || canonicalProduct;

    // Read the requested print-area role
    const safeRoleCode = String(roleCode || configuredRole?.role || "").trim();

    // Stop when the role cannot be identified
    if (!safeRoleCode) { return null; }

    // Read the configured role's allowed aspects and rotations
    const roleViewSettings = normaliseRoleViewSettings(configuredRole?.view_settings);

    // Read product surfaces safely
    const surfaces = Array.isArray(customProduct?.surfaces) ? customProduct.surfaces : [];

    // Store every live view available to this configured product role
    const availableViews = [];

    // Check surfaces in their existing product order
    surfaces.forEach((surface) => {

        // Read print areas safely
        const printAreas = Array.isArray(surface?.print_areas) ? surface.print_areas : [];

        // Check print areas in their existing product order
        printAreas.forEach((printArea) => {

            // Only use the print area belonging to the configured role
            if (String(printArea?.role || "").trim() !== safeRoleCode) { return; }

            // Read the real product views for this print area
            const views = Array.isArray(printArea?.views) ? printArea.views : [];

            // Keep only views that this configured role can actually use
            views.forEach((view) => {

                // Skip inactive views
                if (String(view?.active || "Y").trim().toUpperCase() === "N") { return; }

                // A live product preview requires a preview asset
                const assets = Array.isArray(view?.assets) ? view.assets : [];

                // Check whether this view can be rendered as a live preview
                const hasPreviewAsset = assets.some((asset) => {
                    return String(asset?.type || "").trim().toLowerCase() === "preview";
                });

                // Skip views that cannot render as live product previews
                if (!hasPreviewAsset) { return; }

                // Read every design aspect this real product view can display
                const viewAspects = normaliseProductViewAspects(view);

                // Read rotation from either supported field name
                let rotationValue = view?.rotation;

                if (rotationValue === null || rotationValue === undefined || rotationValue === "") {
                    rotationValue = view?.rotate;
                }

                // Convert the rotation to a number
                const rotation = Number(rotationValue);

                // Skip malformed view data
                if (viewAspects.length === 0) { return; }
                if (!SUPPORTED_VIEW_ROTATIONS.includes(rotation)) { return; }

                // Check whether the real view and configured role share at least one aspect
                const hasCompatibleAspect = roleViewSettings.aspects.some((aspect) => viewAspects.includes(aspect));

                // Skip views excluded by the configured product role
                if (!hasCompatibleAspect) { return; }
                if (!roleViewSettings.rotations.includes(rotation)) { return; }

                // Store the exact view plus the context required by the preview module
                availableViews.push({
                    ...view,
                    surface_code: view?.surface_code || surface?.code || "default",
                    print_area_code: view?.print_area_code || printArea?.code || "default",
                    role: view?.role || printArea?.role || safeRoleCode,
                });

            });

        });

    });

    // Match the same reference/default ordering used by the product image carousel
    availableViews.sort((firstView, secondView) => {
        return getProductPreviewViewPriority(firstView) - getProductPreviewViewPriority(secondView);
    });

    // Return the first configured design-product view
    return availableViews[0] || null;

};



export const normaliseMockupViewSettings = (viewSettings) => {

    // Mockups without view settings are not compatible with any configured role
    return normaliseViewSettings(viewSettings, {
        aspects: [],
        rotations: [],
    });

};

export const getCanonicalProductMockups = (canonicalProduct) => {

    // Accept either a canonical POD product or an already-expanded custom product
    const customProduct = canonicalProduct?.custom_product || canonicalProduct;

    // Read the mockup ids selected for this canonical POD product
    const configuredMockupIds = Array.isArray(canonicalProduct?.custom_product_config?.mockups)
        ? new Set(
            canonicalProduct.custom_product_config.mockups
                .map((mockupId) => Number(mockupId))
                .filter((mockupId) => mockupId)
        )
        : null;

    // Store unique mockups in their canonical order
    const mockups = [];
    const addedMockupIds = new Set();

    // Add a canonical mockup once
    const addMockup = (mockup) => {

        // Read the mockup id from either supported response shape
        const mockupId = Number(mockup?.id || mockup?.cp_mockups_id);

        // Skip invalid or duplicate mockups
        if (!mockupId || addedMockupIds.has(mockupId)) { return; }

        // Respect the canonical POD product's selected mockup ids
        if (configuredMockupIds?.size > 0 && !configuredMockupIds.has(mockupId)) { return; }

        // Store the mockup
        addedMockupIds.add(mockupId);
        mockups.push(mockup);

    };

    // The canonical POD product response supplies its selected mockups here
    const canonicalMockups = Array.isArray(customProduct?.mockups)
        ? customProduct.mockups
        : [];

    canonicalMockups.forEach((mockup) => addMockup(mockup));

    // Return immediately when the canonical response supplied its mockups
    if (mockups.length > 0) { return mockups; }

    // Retain support for older responses which nested mockups under print areas
    const surfaces = Array.isArray(customProduct?.surfaces)
        ? customProduct.surfaces
        : [];

    surfaces.forEach((surface) => {

        // Read print areas safely
        const printAreas = Array.isArray(surface?.print_areas)
            ? surface.print_areas
            : [];

        printAreas.forEach((printArea) => {

            // Add mockups from the legacy nested response shape
            const locationMockups = Array.isArray(printArea?.mockups)
                ? printArea.mockups
                : [];

            locationMockups.forEach((mockup) => addMockup(mockup));

        });

    });

    // Return the canonical product's available mockups
    return mockups;

};

export const normaliseRotationConfig = (rotationConfig) => {

    // A generated mockup configuration must contain physical product locations
    if (!Array.isArray(rotationConfig) || rotationConfig.length < 1) { return []; }

    const locationsByKey = new Map();

    // Validate and store every physical location once
    for (const location of rotationConfig) {

        const productCode = String(location?.product_code || "").trim();
        const surfaceCode = String(location?.surface_code || "").trim();
        const printAreaCode = String(location?.print_area_code || "").trim();
        const hasRotation = location?.rotation !== null && location?.rotation !== undefined && location?.rotation !== "";
        const rotation = Number(location?.rotation);

        // Reject malformed physical locations
        if (!productCode || !surfaceCode || !printAreaCode || !hasRotation || !SUPPORTED_VIEW_ROTATIONS.includes(rotation)) { return []; }

        const locationKey = productCode + "|" + surfaceCode + "|" + printAreaCode;

        // Duplicate physical locations make the configuration ambiguous
        if (locationsByKey.has(locationKey)) { return []; }

        locationsByKey.set(locationKey, {
            product_code: productCode,
            surface_code: surfaceCode,
            print_area_code: printAreaCode,
            rotation: rotation,
        });

    }

    // Keep configuration identity stable regardless of source array order
    return Array.from(locationsByKey.keys())
        .sort()
        .map((locationKey) => locationsByKey.get(locationKey));

};

export const getRotationConfigKey = (rotationConfig) => {

    const normalisedRotationConfig = normaliseRotationConfig(rotationConfig);

    // Invalid or empty configurations do not have an identity
    if (normalisedRotationConfig.length < 1) { return ""; }

    return JSON.stringify(normalisedRotationConfig);

};

export const getMockupRotationConfigKey = (mockupId, rotationConfig) => {

    const safeMockupId = String(mockupId || "");
    const rotationConfigKey = getRotationConfigKey(rotationConfig);

    // Both logical mockup and physical rotations are required
    if (!safeMockupId || !rotationConfigKey) { return ""; }

    return safeMockupId + "|" + rotationConfigKey;

};

export const getAvailableMockupConfigurationsForConfiguredRoles = (canonicalProduct, configuredRoles) => {

    // Accept either a canonical POD product or its expanded custom product
    const customProduct = canonicalProduct?.custom_product || canonicalProduct;

    // Read the canonical mockups and physical product structure
    const selectedMockups = getCanonicalProductMockups(canonicalProduct);
    const surfaces = Array.isArray(customProduct?.surfaces) ? customProduct.surfaces : [];

    // Resolve the exact custom product code used by mockup locations
    const customProductCode = String(customProduct?.product_code || customProduct?.code || "").trim();

    if (!customProductCode || selectedMockups.length < 1 || surfaces.length < 1) { return []; }

    // Build the active configured-role compatibility lookup
    const configuredRoleMap = {};
    const safeConfiguredRoles = Array.isArray(configuredRoles) ? configuredRoles : [];

    safeConfiguredRoles.forEach((configuredRole) => {

        const roleCode = String(configuredRole?.role || "").trim();

        // Missing and inactive roles do not participate in generated mockups
        if (!roleCode || String(configuredRole?.active || "Y").trim().toUpperCase() === "N") { return; }

        configuredRoleMap[roleCode] = normaliseRoleViewSettings(configuredRole?.view_settings);

    });

    if (Object.keys(configuredRoleMap).length < 1) { return []; }

    // Build the usable physical locations on this configured product
    const productLocations = {};

    surfaces.forEach((surface) => {

        const surfaceCode = String(surface?.code || "").trim();
        const printAreas = Array.isArray(surface?.print_areas) ? surface.print_areas : [];

        if (!surfaceCode) { return; }

        printAreas.forEach((printArea) => {

            const printAreaCode = String(printArea?.code || "").trim();
            const roleCode = String(printArea?.role || "").trim();
            const roleViewSettings = configuredRoleMap[roleCode] || null;
            const views = Array.isArray(printArea?.views) ? printArea.views : [];

            // Only print areas backed by an active configured role can participate
            if (!printAreaCode || !roleViewSettings || views.length < 1) { return; }

            productLocations[surfaceCode + "|" + printAreaCode] = {
                surface_code: surfaceCode,
                print_area_code: printAreaCode,
                role: roleCode,
                role_view_settings: roleViewSettings,
                views: views,
            };

        });

    });

    if (Object.keys(productLocations).length < 1) { return []; }

    const configurations = [];
    const addedConfigurationKeys = new Set();

    // Resolve every logical mockup independently
    selectedMockups.forEach((mockup) => {

        const mockupId = Number(mockup?.id || mockup?.cp_mockups_id);
        const locations = Array.isArray(mockup?.locations) ? mockup.locations : [];

        if (!mockupId || locations.length < 1) { return; }

        const rotationOptionsByLocation = {};
        let mockupValid = true;

        // Resolve every participating physical location in this mockup
        locations.forEach((location) => {

            if (!mockupValid) { return; }
            if (String(location?.product_code || "") !== customProductCode) { return; }

            const surfaceCode = String(location?.surface_code || "").trim();
            const printAreaCode = String(location?.print_area_code || "").trim();
            const productLocation = productLocations[surfaceCode + "|" + printAreaCode] || null;

            // Locations without an active configured role do not participate in this render
            if (!surfaceCode || !printAreaCode || !productLocation) { return; }

            const mockupViewSettings = normaliseMockupViewSettings(location?.view_settings);
            const roleViewSettings = productLocation.role_view_settings;
            const validRotations = [];

            // Find every real product view which this role and this exact mockup location can both use
            productLocation.views.forEach((view) => {

                if (String(view?.active || "Y").trim().toUpperCase() === "N") { return; }

                const viewAspects = normaliseProductViewAspects(view);
                const hasRotation = view?.rotation !== null && view?.rotation !== undefined && view?.rotation !== "";
                const rotation = Number(view?.rotation);

                if (viewAspects.length < 1 || !hasRotation || !SUPPORTED_VIEW_ROTATIONS.includes(rotation)) { return; }
                if (!roleViewSettings.rotations.includes(rotation)) { return; }
                if (!mockupViewSettings.rotations.includes(rotation)) { return; }

                // Aspect is a compatibility test only; it is not generated-image identity
                const aspectMatches = roleViewSettings.aspects.some((aspect) => {
                    return viewAspects.includes(aspect) && mockupViewSettings.aspects.includes(aspect);
                });

                if (!aspectMatches) { return; }

                if (!validRotations.includes(rotation)) { validRotations.push(rotation); }

            });

            // Every participating location must have at least one usable rotation
            if (validRotations.length < 1) {
                mockupValid = false;
                return;
            }

            validRotations.sort((firstRotation, secondRotation) => firstRotation - secondRotation);

            const rotationLocationKey = customProductCode + "|" + surfaceCode + "|" + printAreaCode;

            rotationOptionsByLocation[rotationLocationKey] = {
                product_code: customProductCode,
                surface_code: surfaceCode,
                print_area_code: printAreaCode,
                rotations: validRotations,
            };

        });

        const rotationOptionKeys = Object.keys(rotationOptionsByLocation).sort();

        if (!mockupValid || rotationOptionKeys.length < 1) { return; }

        let rotationConfigurations = [[]];

        // Expand independent physical-location rotations into complete render configurations
        rotationOptionKeys.forEach((rotationOptionKey) => {

            const rotationOption = rotationOptionsByLocation[rotationOptionKey];
            const nextRotationConfigurations = [];

            rotationConfigurations.forEach((rotationConfiguration) => {

                rotationOption.rotations.forEach((rotation) => {

                    nextRotationConfigurations.push([
                        ...rotationConfiguration,
                        {
                            product_code: rotationOption.product_code,
                            surface_code: rotationOption.surface_code,
                            print_area_code: rotationOption.print_area_code,
                            rotation: rotation,
                        },
                    ]);

                });

            });

            rotationConfigurations = nextRotationConfigurations;

        });

        // Store each complete rotation configuration once for this logical mockup
        rotationConfigurations.forEach((rotationConfig) => {

            const normalisedRotationConfig = normaliseRotationConfig(rotationConfig);
            const configurationKey = getMockupRotationConfigKey(mockupId, normalisedRotationConfig);

            if (!configurationKey || addedConfigurationKeys.has(configurationKey)) { return; }

            addedConfigurationKeys.add(configurationKey);

            configurations.push({
                key: configurationKey,
                mockup_id: mockupId,
                rotation_config: normalisedRotationConfig,
                mockup: mockup,
            });

        });

    });

    return configurations;

};

export const removeIncompatibleMockupImages = (
    images,
    canonicalProduct,
    configuredRoles
) => {

    // Normalise current image rows
    const safeImages = Array.isArray(images) ? images : [];

    // Build every exact generated configuration still supported by the configured roles
    const availableConfigurations =
        getAvailableMockupConfigurationsForConfiguredRoles(
            canonicalProduct,
            configuredRoles
        );

    // Index the legal generated configurations by logical mockup and complete rotation configuration
    const configurationMap = new Map();

    availableConfigurations.forEach((configuration) => {

        const configurationKey = String(configuration?.key || "");

        // Ignore malformed configurations
        if (!configurationKey) { return; }

        configurationMap.set(configurationKey, configuration);

    });

    // Store compatible rows and prevent duplicate generated configurations
    const compatibleImages = [];
    const addedConfigurationKeys = new Set();

    safeImages.forEach((imageRow) => {

        // Skip invalid rows
        if (!imageRow || typeof imageRow !== "object") { return; }

        // Manual bucket and canonical product images are not restricted by product view settings
        if (!imageRow.mockup_id) {
            if (imageRow.image_id) { compatibleImages.push(imageRow); }
            return;
        }

        // Generated rows under the new model must carry their complete physical rotation configuration
        const rotationConfig = normaliseRotationConfig(imageRow?.rotation_config);
        const configurationKey = getMockupRotationConfigKey(
            imageRow.mockup_id,
            rotationConfig
        );

        // Missing, malformed or no-longer-compatible generated rows are removed
        if (!configurationKey || !configurationMap.has(configurationKey)) { return; }

        // Prevent duplicate exact generated configurations
        if (addedConfigurationKeys.has(configurationKey)) { return; }

        const resolvedConfiguration = configurationMap.get(configurationKey);

        addedConfigurationKeys.add(configurationKey);

        // Keep the existing generated result when its exact configuration is still legal
        compatibleImages.push({
            ...imageRow,
            mockup_id: Number(resolvedConfiguration.mockup_id),
            rotation_config: normaliseRotationConfig(resolvedConfiguration.rotation_config),
        });

    });

    // Ensure a configured product with legal mockups retains at least one generated row
    const hasMockupImage = compatibleImages.some((imageRow) => Boolean(imageRow?.mockup_id));

    if (!hasMockupImage && availableConfigurations.length > 0) {

        const firstConfiguration = availableConfigurations[0];
        const rotationConfig = normaliseRotationConfig(firstConfiguration?.rotation_config);

        // Add the first legal generated configuration before any manual images
        if (firstConfiguration?.mockup_id && rotationConfig.length > 0) {

            compatibleImages.unshift({
                image_id: null,
                mockup_id: Number(firstConfiguration.mockup_id),
                rotation_config: rotationConfig,
                image_url: null,
                thumbnail_url: null,
            });

        }

    }

    // Rebuild ordering after removal or automatic insertion
    return compatibleImages.map((imageRow, imageIndex) => ({
        ...imageRow,
        ordering: imageIndex + 1,
    }));

};
